import {
    type AccountSummary,
    type HolisticSummaryCategory,
    Provider,
    type SpendAccountCategory,
} from '@fruitful/types';
import { AnimatedScrollView } from '@kanelloc/react-native-animated-header-scroll-view';
import { Redirect, router } from 'expo-router';
import { useAtomValue } from 'jotai';
import { capitalize } from 'lodash';
import * as React from 'react';
import { useEffect, useState } from 'react';
import Animated, { FadeIn } from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
    BodyMediumRegular,
    Button,
    ButtonSize,
    ButtonVariant,
    HolisticAccountLogo,
    Icon,
    isNonMobileWeb,
    PageBelowHeader,
    PenLineIcon,
    PillButton,
    RotateCcwIcon,
    Sheet,
    Spinner,
    UnlinkIcon,
    useToastController,
    View,
    YStack,
} from 'smoothie';

import useGetAllBankAccountDetails from '../../banking/hooks/useGetAllBankAccountDetails';
import { FRUITFUL_INSTITUTION_NAME } from '../../banking/types/BankAccount';
import { AddInstitutionSheet } from '../../common/components/AddInstitutionSheet';
import { useAuthedUser } from '../../common/providers/AuthedUserProvider';
import {
    groupAccounts,
    isAccountInErrorState,
} from '../../common/utils/holisticUtils';
import LogRocketFacade from '../../logger/log-rocket/LogRocketFacade';
import { captureException } from '../../logger/Logger';
import useGetActiveMoneyMap from '../../roadmap/hooks/useGetActiveMoneyMap';
import {
    CATEGORY_INFO_MAP,
    FRUITFUL_ALL_ACCOUNT_DISCLOSURES,
} from '../constants';
import useConnectQuilttInstitution from '../hooks/useConnectQuilttInstitution';
import useGetHolisticSummary from '../hooks/useGetHolisticSummary';
import useRecategorizeAccount from '../hooks/useRecategorizeAccount';
import useReconnectQuilttInstitution from '../hooks/useReconnectQuilttInstitution';
import useRenameAccount from '../hooks/useRenameAccount';
import useToggleAccountVisibility from '../hooks/useToggleAccountVisibility';
import { holisticSummaryAtom } from '../state/HolisticAtoms';
import {
    type AccountGroups,
    type GroupedAccount,
} from '../types/HolisticSummary';
import AccountErrorSheet from './AccountErrorSheet';
import { AccountRow, AccountRowGroupHeader } from './AccountRow';
import EditAccountSheet, { EditSheetPage } from './EditAccountSheet';
import { HolisticCategoryHeader } from './headers/HolisticCategoryHeader';
import { HolisticNavHeader } from './headers/HolisticNavHeader';

interface CategoryOverviewProps {
    category: HolisticSummaryCategory;
}

const CategoryOverview = ({ category }: CategoryOverviewProps) => {
    LogRocketFacade.tagPage('CategoryOverview');

    const user = useAuthedUser();
    const toast = useToastController();

    const { infoSheetTitle, infoSheetDescription } =
        CATEGORY_INFO_MAP[category];

    const holisticSummary = useAtomValue(holisticSummaryAtom);
    const {
        refetch: refetchHolisticSummary,
        isLoading: isRefetchingHolisticSummary,
        isError: holisticSummaryHasError,
    } = useGetHolisticSummary(!holisticSummary?.[category]);

    const { bankAccountDetails, isLoading: isLoadingBankAccountDetails } =
        useGetAllBankAccountDetails();
    const { moneyMap, isLoadingMoneyMap } = useGetActiveMoneyMap();

    const [groupedAccounts, setGroupedAccounts] = useState<
        AccountGroups | undefined
    >();
    useEffect(() => {
        setGroupedAccounts(
            groupAccounts(
                category,
                holisticSummary?.[category]?.accounts ?? [],
                moneyMap,
                bankAccountDetails,
            ),
        );
    }, [category, holisticSummary?.[category]?.accounts]);

    const [selectedAccount, setSelectedAccount] =
        useState<GroupedAccount | null>(null);
    const [isInfoSheetOpen, setIsInfoSheetOpen] = useState(false);
    const [isEditSheetOpen, setIsEditSheetOpen] = useState(false);
    const [isErrorSheetOpen, setIsErrorSheetOpen] = useState(false);
    const [isDisclosureSheetOpen, setIsDisclosureSheetOpen] = useState(false);
    const [editSheetPage, setEditSheetPage] = useState(EditSheetPage.EDIT);
    const [isInstitutionSheetOpen, setIsInstitutionSheetOpen] = useState(false);
    const [
        institutionSheetSpendAccountCategory,
        setInstitutionSheetSpendAccountCategory,
    ] = useState<SpendAccountCategory | undefined>(undefined);

    const { connectInstitution } = useConnectQuilttInstitution();
    const { reconnectInstitution } = useReconnectQuilttInstitution(
        selectedAccount?.connectionId as string,
    );

    const getRelinkingFlow = (account: GroupedAccount) => {
        const { provider } = account;

        if (provider === Provider.QUILTT) {
            return reconnectInstitution;
        }
        return connectInstitution;
    };

    const insets = useSafeAreaInsets();

    const { mutateAsync: renameAccount, isPending: isLoadingRename } =
        useRenameAccount();
    const {
        mutateAsync: recategorizeAccount,
        isPending: isLoadingRecategorize,
    } = useRecategorizeAccount();
    const {
        mutateAsync: toggleAccountVisibility,
        isPending: isLoadingToggleVisibility,
    } = useToggleAccountVisibility();

    const handleAccountPress = (account: GroupedAccount) => {
        setSelectedAccount(account);
        if (isAccountInErrorState(account)) {
            setIsErrorSheetOpen(true);
        } else {
            if (account.provider === Provider.MANUAL) {
                router.navigate({
                    pathname: '/holistic/[category]/manual/[id]',
                    params: { category, id: account.groupedAccountId },
                });
            } else if (
                account.institutionName === FRUITFUL_INSTITUTION_NAME &&
                account.provider === Provider.SYNCTERA &&
                account.userId === user.id // If the current user is listed here, they should have access to view in banking
            ) {
                router.navigate({
                    pathname: '/banking/space-details',
                    params: {
                        syncteraAccountId: account.id,
                    },
                });
            } else if (
                account.provider === Provider.APEX &&
                account.userId === user.id
            ) {
                router.navigate({
                    pathname: '/investments/account-overview',
                    params: {
                        apexAccountId: account.id,
                    },
                });
            } else {
                router.navigate({
                    pathname: `/holistic/[category]/account/[id]`,
                    params: {
                        category,
                        id: account.groupedAccountId,
                        connectionId: account.connectionId,
                    },
                });
            }
        }
    };

    const onSubmitRename = async (
        accountId: string,
        provider: Provider,
        rename: string,
    ) => {
        try {
            await renameAccount({
                body: {
                    accountId,
                    provider,
                    name: rename,
                },
            });
            void refetchHolisticSummary();
            toast.show('Account renamed', {
                duration: 2000,
                icon: (
                    <View
                        backgroundColor="$gray100"
                        padding={8}
                        borderRadius={4}
                    >
                        <Icon icon={PenLineIcon} size={20} color="$gray1100" />
                    </View>
                ),
            });
        } catch (e) {
            captureException(e);
            toast.show('Error renaming account', {
                duration: 2000,
            });
        }
    };

    const onSubmitRecategorizeAccount = async (
        account: AccountSummary,
        holisticCategory: HolisticSummaryCategory,
        spendAccountCategory?: SpendAccountCategory,
    ) => {
        try {
            const {
                id: accountId,
                name,
                institutionName,
                logo,
                provider,
            } = account;
            await recategorizeAccount({
                body: {
                    accountId,
                    provider,
                    holisticCategory,
                    spendAccountCategory,
                },
            });
            void refetchHolisticSummary();
            toast.show(
                `${name ?? 'Account'} moved to ${capitalize(holisticCategory)}`,
                {
                    duration: 2000,
                    icon: (
                        <HolisticAccountLogo
                            institutionName={institutionName}
                            logo={logo}
                            provider={provider}
                        />
                    ),
                },
            );
        } catch (e) {
            captureException(e);
            toast.show('Error recategorizing account', {
                duration: 2000,
            });
        }
    };

    const onSubmitHideAccount = async (
        accountId: string,
        provider: Provider,
        isLastAccountOnLink: boolean,
    ) => {
        try {
            await toggleAccountVisibility({
                body: {
                    accountId,
                    provider,
                    isHidden: true,
                },
            });

            void refetchHolisticSummary();

            toast.show('Account has been hidden', {
                duration: 4000,
                icon: (
                    <View
                        backgroundColor="$gray100"
                        padding={8}
                        borderRadius={4}
                    >
                        <Icon icon={UnlinkIcon} size={20} color="$gray1100" />
                    </View>
                ),
                ...(isLastAccountOnLink
                    ? {}
                    : {
                          button: (
                              <Button
                                  variant={ButtonVariant.PRIMARY}
                                  size={ButtonSize.SMALL}
                                  width="auto"
                                  text="Undo"
                                  icon={RotateCcwIcon}
                                  onPress={() => {
                                      void handleUndoHide(accountId, provider);
                                  }}
                              />
                          ),
                      }),
            });
        } catch (e) {
            // Still refresh in case this call partially worked
            void refetchHolisticSummary();

            captureException(e);
            toast.show('Error unlinking account', {
                duration: 2000,
            });
        }
    };

    const handleUndoHide = async (accountId: string, provider: Provider) => {
        try {
            toast.hide();
            await toggleAccountVisibility({
                body: {
                    accountId,
                    provider,
                    isHidden: false,
                },
            });
            void refetchHolisticSummary();
        } catch (e) {
            captureException(e);
            toast.show('Error undoing unlink account', {
                duration: 2000,
            });
        }
    };

    // Use a custom back function because sometimes on native we've seen pressing
    // 'back' from this screen can somehow crash the app
    const onPressBack = () => {
        if (router.canGoBack()) {
            router.back();
        } else {
            router.navigate('/home');
        }
    };

    if (CATEGORY_INFO_MAP[category] == null) {
        return <Redirect href="/home" />;
    }

    const isLoading =
        isRefetchingHolisticSummary ||
        isLoadingRename ||
        isLoadingToggleVisibility ||
        isLoadingRecategorize ||
        isLoadingBankAccountDetails ||
        isLoadingMoneyMap;

    const topBarHeight = (insets.top || 16) + 48;

    return (
        <View flex={1} backgroundColor="$gray100">
            <AnimatedScrollView
                style={{ flex: 1 }}
                isHeaderComponentFixed={true}
                isFullScreen={true}
                contentContainerStyle={{ flexGrow: 1 }}
                showsVerticalScrollIndicator={isNonMobileWeb}
                alwaysBounceVertical={false}
                topBarHeight={topBarHeight}
                headerMaxHeight={280}
                HeaderComponent={
                    <HolisticCategoryHeader
                        category={category}
                        totalCurrentBalanceCents={
                            holisticSummary?.[category]
                                ?.totalCurrentBalanceCents
                        }
                        onPressInfoIcon={() => {
                            setIsInfoSheetOpen(true);
                        }}
                    />
                }
                HeaderNavbarComponent={
                    <HolisticNavHeader
                        onPressBack={onPressBack}
                        isScrolled={false}
                        height={topBarHeight}
                    />
                }
                TopNavBarComponent={
                    <HolisticNavHeader
                        title={capitalize(category as string)}
                        onPressInfoIcon={() => {
                            setIsInfoSheetOpen(true);
                        }}
                        onPressBack={onPressBack}
                        isScrolled={true}
                        height={topBarHeight}
                    />
                }
            >
                <PageBelowHeader
                    maxWidth={700}
                    $sm={{
                        height: 'auto',
                        padding: 40,
                        marginBottom: 80,
                        borderRadius: 32,
                    }}
                >
                    {isLoading ? (
                        <Spinner marginVertical={48} />
                    ) : holisticSummaryHasError ? (
                        <YStack
                            alignItems="center"
                            justifyContent="center"
                            marginVertical={36}
                            gap={16}
                        >
                            <BodyMediumRegular color="$gray1000">
                                Whoops, our account link provider hit an error
                            </BodyMediumRegular>
                            <PillButton
                                text="Try again"
                                onPress={() => {
                                    void refetchHolisticSummary();
                                }}
                            />
                        </YStack>
                    ) : (
                        <Animated.View entering={FadeIn.duration(500)}>
                            {Object.values(groupedAccounts ?? {}).map(
                                ({
                                    accountGroupCategory,
                                    heading,
                                    accounts,
                                }) => {
                                    return (
                                        <YStack marginBottom={20} key={heading}>
                                            <AccountRowGroupHeader
                                                accountGroupCategory={
                                                    accountGroupCategory
                                                }
                                                heading={heading}
                                                openDisclosureSheet={() => {
                                                    setIsDisclosureSheetOpen(
                                                        true,
                                                    );
                                                }}
                                                openInstitutionSheet={() => {
                                                    setIsInstitutionSheetOpen(
                                                        true,
                                                    );
                                                }}
                                                setInstitutionSheetSpendAccountCategory={
                                                    setInstitutionSheetSpendAccountCategory
                                                }
                                            />
                                            {accounts.map((account) => (
                                                <AccountRow
                                                    key={account.id}
                                                    account={account}
                                                    onPress={() => {
                                                        handleAccountPress(
                                                            account,
                                                        );
                                                    }}
                                                />
                                            ))}
                                        </YStack>
                                    );
                                },
                            ) ?? null}
                        </Animated.View>
                    )}
                </PageBelowHeader>
            </AnimatedScrollView>
            <AddInstitutionSheet
                holisticCategory={category}
                spendType={institutionSheetSpendAccountCategory}
                isOpen={isInstitutionSheetOpen}
                onClose={() => {
                    setIsInstitutionSheetOpen(false);
                }}
                launchInstitutionConnector={() => {
                    setIsInstitutionSheetOpen(false);
                    connectInstitution();
                }}
            />
            <Sheet
                title={infoSheetTitle}
                isOpen={isInfoSheetOpen}
                onClose={() => {
                    setIsInfoSheetOpen(false);
                }}
                showCloseButton={false}
                forceKeyboardCompatibleBottomSheet
            >
                <YStack paddingTop={12} gap={50} $sm={{ gap: 80 }}>
                    <BodyMediumRegular>
                        {infoSheetDescription}
                    </BodyMediumRegular>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        text="Got it"
                        onPress={() => {
                            setIsInfoSheetOpen(false);
                        }}
                    />
                </YStack>
            </Sheet>
            <Sheet
                title="Your Fruitful accounts"
                isOpen={isDisclosureSheetOpen}
                onClose={() => {
                    setIsDisclosureSheetOpen(false);
                }}
                showCloseButton={false}
                forceKeyboardCompatibleBottomSheet
            >
                <YStack paddingTop={12} gap={50} $sm={{ gap: 80 }}>
                    <BodyMediumRegular>
                        {FRUITFUL_ALL_ACCOUNT_DISCLOSURES}
                    </BodyMediumRegular>
                    <Button
                        variant={ButtonVariant.PRIMARY}
                        text="Got it"
                        onPress={() => {
                            setIsDisclosureSheetOpen(false);
                        }}
                    />
                </YStack>
            </Sheet>
            {selectedAccount && (isEditSheetOpen || isErrorSheetOpen) && (
                <>
                    {isEditSheetOpen && (
                        <EditAccountSheet
                            account={selectedAccount}
                            page={editSheetPage}
                            setPage={setEditSheetPage}
                            onClose={() => {
                                setIsEditSheetOpen(false);
                                setEditSheetPage(EditSheetPage.EDIT);
                            }}
                            onSubmitRename={onSubmitRename}
                            onSubmitRecategorizeAccount={
                                onSubmitRecategorizeAccount
                            }
                            onSubmitHideAccount={onSubmitHideAccount}
                            onPressRelink={() => {
                                setIsEditSheetOpen(false);
                                getRelinkingFlow(selectedAccount)();
                            }}
                        />
                    )}
                    <AccountErrorSheet
                        account={selectedAccount}
                        isOpen={isErrorSheetOpen}
                        onClose={() => {
                            setIsErrorSheetOpen(false);
                        }}
                        onPressRelink={getRelinkingFlow(selectedAccount)}
                        onPressRecategorize={() => {
                            setEditSheetPage(EditSheetPage.RECATEGORIZE);
                            setIsEditSheetOpen(true);
                        }}
                        onPressHide={() => {
                            setIsErrorSheetOpen(false);
                            setEditSheetPage(EditSheetPage.CONFIRM_HIDE);
                            setIsEditSheetOpen(true);
                        }}
                    />
                </>
            )}
        </View>
    );
};

export default CategoryOverview;
